"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, TrendingUp, Search, Target, Users, CircleCheck as CheckCircle2, ArrowRight, ChartBar as BarChart3, Building2, Compass, Zap } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function Wolverhampton() {
  const shouldReduceMotion = useReducedMotion();
  const marketStats = [{
    value: "74%",
    title: "Mobile Local Intent",
    description: "Of Wolverhampton search queries exhibit immediate commercial buying intent.",
    badge: "High Conversion"
  }, {
    value: "3.2x",
    title: "Map Pack Advantage",
    description: "Higher click-through rate for businesses holding top 3 positions in local search.",
    badge: "Visibility Boost"
  }, {
    value: "61%",
    title: "Competitor Gap",
    description: "Of local West Midlands businesses lack structured technical local SEO schema.",
    badge: "Key Opportunity"
  }];
  const behaviorInsights = [{
    icon: Search,
    title: "Near-Me Commercial Query Surge",
    description: "Wolverhampton decision-makers and consumers rely heavily on localized terms combining business services with regional proximity rather than generic UK-wide terms."
  }, {
    icon: Target,
    title: "High-Intent B2B & Retail Traffic",
    description: "From manufacturing hubs to professional services around Wolverhampton Science Park, commercial searches demand specific expertise and transparent proof."
  }, {
    icon: Compass,
    title: "Multi-Location Radius Search",
    description: "Prospects search fluidly across Wolverhampton, Dudley, Walsall, and Telford. Winning local rankings requires a smart regional hub strategy."
  }];
  const strategySteps = [{
    number: "01",
    title: "Hyper-Local Content Positioning",
    description: "I build tailored, non-fluff landing pages tuned to Wolverhampton search behavior and regional terminology."
  }, {
    number: "02",
    title: "Google Business Profile Optimization",
    description: "I systematically optimize your local profile, citation network, and geotagged trust signals to command the Map Pack."
  }, {
    number: "03",
    title: "Technical Authority & Speed",
    description: "I fix crawl errors, implement schema markup, and maximize page speed so your site converts local visitors immediately."
  }];
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const staggerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.12
      }
    }
  };
  return <section id="wolverhamptonmarketlandscape" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 overflow-hidden relative">
      <div className="max-w-7xl mx-auto space-y-16">
        
        {/* Section Heading */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={fadeInVariants} className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/80 border border-border text-foreground text-xs font-medium">
            <MapPin className="w-3.5 h-3.5 text-accent" />
            <span>Regional Market Analysis</span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-primary font-default">
            Wolverhampton Local Search Landscape & Market Opportunity
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Understanding how Wolverhampton customers search is the key to outranking local competitors. 
            When you work directly with me, I engineer tailored SEO strategies that capture high-converting search intent across the West Midlands.
          </p>
        </motion.div>

        {/* Key Market Stats Grid */}
        <motion.div variants={staggerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {marketStats.map((stat, i) => <motion.div key={i} data-index={i} variants={fadeInVariants}>
              <Card className="bg-card border-border hover:border-accent/40 transition-[border-color,box-shadow] duration-300 shadow-sm relative overflow-hidden h-full flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-24 h-24 bg-accent/10 rounded-bl-full pointer-events-none" />
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-normal">
                      {stat.badge}
                    </Badge>
                    <BarChart3 className="w-5 h-5 text-accent" />
                  </div>
                  <div className="text-4xl sm:text-5xl font-bold text-primary mt-4 font-default">
                    {stat.value}
                  </div>
                  <CardTitle className="text-lg font-semibold text-foreground font-default mt-2">
                    {stat.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-sm text-muted-foreground leading-normal">
                    {stat.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Local Insights & Behavior */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center pt-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.3
        }} variants={fadeInVariants} className="lg:col-span-5 space-y-6">
            <div className="inline-block px-3 py-1 rounded-md bg-secondary text-accent text-xs font-semibold uppercase tracking-wider">
              Search Behavior Insights
            </div>
            
            <h3 className="text-2xl sm:text-3xl font-semibold text-primary tracking-tight font-default">
              How Wolverhampton Customers Search & Choose Experts
            </h3>

            <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
              Generic nationwide SEO tactics fail because they ignore regional nuances. In Wolverhampton, decision-makers want clear authority, transparent deliverables, and fast direct access to experts.
            </p>

            <div className="p-4 rounded-xl bg-muted/60 border border-border space-y-3">
              <div className="flex items-center gap-2 text-foreground font-medium text-sm">
                <Zap className="w-4 h-4 text-accent" />
                <span>Direct Consultant Access</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                You work directly with me—Yassine Essaadi—not a junior account manager or an automated agency queue. I personally analyze, build, and optimize your local Wolverhampton search engine presence.
              </p>
            </div>

            <div className="pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm">
                <Link to="/ContactPage">
                  <span>Book a Free Wolverhampton SEO Consultation</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div variants={staggerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7 space-y-4">
            {behaviorInsights.map((insight, i) => {
            const Icon = insight.icon;
            return <motion.div key={i} data-index={i} variants={fadeInVariants}>
                  <div className="p-6 rounded-xl bg-card border border-border hover:border-accent/40 transition-[border-color,box-shadow] duration-300 shadow-xs flex flex-col sm:flex-row gap-4 items-start">
                    <div className="p-3 rounded-lg bg-secondary text-accent shrink-0">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="space-y-1.5">
                      <h4 className="text-lg font-semibold text-foreground font-default">
                        {insight.title}
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {insight.description}
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Wolverhampton Action Steps */}
        <div className="rounded-2xl bg-muted/40 border border-border p-8 sm:p-12 space-y-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.3
        }} variants={fadeInVariants} className="text-center max-w-2xl mx-auto space-y-3">
            <span className="text-xs font-semibold text-accent uppercase tracking-wider bg-secondary px-3 py-1 rounded-full">
              Action Plan
            </span>
            <h3 className="text-2xl sm:text-3xl font-semibold text-primary font-default">
              My Wolverhampton Search Strategy Roadmap
            </h3>
            <p className="text-sm text-muted-foreground">
              A transparent, outcome-focused approach designed to capture sustainable local revenue.
            </p>
          </motion.div>

          <motion.div variants={staggerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {strategySteps.map((step, i) => <motion.div key={i} data-index={i} variants={fadeInVariants}>
                <div className="p-6 rounded-xl bg-background border border-border shadow-xs h-full flex flex-col justify-between space-y-4">
                  <div className="space-y-3">
                    <span className="text-xs font-bold text-accent bg-secondary px-2.5 py-1 rounded-md inline-block">
                      Step {step.number}
                    </span>
                    <h4 className="text-base font-semibold text-foreground font-default">
                      {step.title}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-primary font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5 text-accent" />
                    <span>Tested Methodology</span>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>

          {/* Inline CTA strip */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.4
        }} variants={fadeInVariants} className="pt-4 text-center flex flex-col sm:flex-row items-center justify-center gap-4 border-t border-border/80">
            <p className="text-sm font-medium text-foreground">
              Ready to claim top search rankings across Wolverhampton?
            </p>
            <div className="flex items-center gap-3">
              <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactPage">Book Your Audit</Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="border-border hover:bg-secondary">
                <Link to="/CaseStudiesPage">View Case Studies</Link>
              </Button>
            </div>
          </motion.div>
        </div>

      </div>
    </section>;
}